/**
 * Meldungen am unteren rechten Rand.
 *
 * Uebernommen aus dem Entwurf (`toast`, `toastZu` und der TOASTS-Abschnitt
 * des Markups). Hoechstens drei gleichzeitig.
 *
 * **Jede** Meldung verschwindet von selbst — auch die Fehlermeldung. Vorher
 * blieb sie stehen, mit der Begruendung, man muesse sie lesen koennen; in der
 * Bedienung war das eine Karte, die den unteren rechten Rand dauerhaft belegte
 * und die man einzeln wegklicken musste.
 *
 * Statt „bleibt stehen" gilt jetzt „laeuft weiter, solange niemand hinschaut":
 * die Uhr haelt an, sobald der Zeiger auf der Meldung steht oder der Fokus in
 * ihr liegt, und laeuft danach neu an. Wer liest, verliert den Text nicht;
 * wer nicht hinsieht, bekommt seinen Bildschirm zurueck. Fehler bekommen
 * dafuer mehr Zeit als Hinweise — sie tragen mehr Text.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { stil } from './stil';

export type Toastart = 'erfolg' | 'hinweis' | 'fehler';

interface Meldung {
  id: number;
  titel: string;
  text: string | null;
  art: Toastart;
  rueckgaengig: (() => void) | null;
}

export type Melden = (
  titel: string,
  text?: string | null,
  art?: Toastart,
  rueckgaengig?: (() => void) | null,
) => void;

/**
 * Standzeit je Art, in Millisekunden.
 *
 * Vier Sekunden reichen fuer „gespeichert". Eine Fehlermeldung nennt Ursache
 * und Folge und braucht laenger; zehn Sekunden sind gemessen an ueblichen
 * Lesegeschwindigkeiten etwa das Doppelte dessen, was die laengste Meldung
 * dieser Anwendung braucht.
 */
const STANDZEIT: Record<Toastart, number> = {
  erfolg: 4000,
  hinweis: 6000,
  fehler: 10000,
};

const ToastKontext = createContext<Melden>(() => {});

/** `const melden = useToast()` — in jeder Ansicht verfuegbar. */
export function useToast(): Melden {
  return useContext(ToastKontext);
}

export function Toasts({ children }: { children: ReactNode }) {
  const [meldungen, setzeMeldungen] = useState<Meldung[]>([]);
  const naechsteId = useRef(1);

  const schliessen = useCallback((id: number) => {
    setzeMeldungen((m) => m.filter((t) => t.id !== id));
  }, []);

  /**
   * Vorgabe ist `hinweis`, nicht `erfolg`.
   *
   * Sie stand auf `erfolg`, und 35 der 39 Aufrufstellen geben keine Art an.
   * Damit quittierte ein gruener Haken Handlungen, die gar nicht stattgefunden
   * haben — unter anderem die Massenaktion „Löschen" der Rollenmatrix und
   * „Antrag abgesendet" im Assistenten. Eine Erfolgsmeldung ist eine Aussage
   * ueber die Wirklichkeit; sie darf nicht der bequeme Vorgabewert sein.
   *
   * Wo wirklich etwas geschieht, steht `'erfolg'` jetzt ausdruecklich da.
   */
  const melden = useCallback<Melden>((titel, text = null, art = 'hinweis', rueckgaengig = null) => {
    const id = naechsteId.current++;
    setzeMeldungen((m) => [...m, { id, titel, text, art, rueckgaengig }].slice(-3));
    // Die Uhr fuehrt jede Karte selbst — nur so laesst sie sich anhalten,
    // solange jemand auf ihr steht.
  }, []);

  const wert = useMemo(() => melden, [melden]);

  return (
    <ToastKontext.Provider value={wert}>
      {children}
      <div
        aria-live="polite"
        style={stil(
          'position:fixed;right:16px;bottom:16px;z-index:var(--z-meldung);display:flex;flex-direction:column;' +
          'gap:10px;width:360px;max-width:calc(100vw - 32px)',
        )}
      >
        {meldungen.map((m) => (
          <Meldungskarte key={m.id} meldung={m} onSchliessen={schliessen} />
        ))}
      </div>
    </ToastKontext.Provider>
  );
}


/* ------------------------------------------------------------ Eine Karte */

/**
 * Eine Meldung samt eigener Uhr.
 *
 * Eigener Baustein, weil die Uhr ein Haken ist: sie muss angehalten und neu
 * gestartet werden koennen, und Haken duerfen nicht in einer Schleife stehen.
 */
function Meldungskarte({ meldung, onSchliessen }: {
  meldung: Meldung;
  onSchliessen: (id: number) => void;
}) {
  const [angehalten, setzeAngehalten] = useState(false);
  const { id, art } = meldung;

  useEffect(() => {
    if (angehalten) return;
    const uhr = window.setTimeout(() => onSchliessen(id), STANDZEIT[art]);
    return () => clearTimeout(uhr);
  }, [id, art, angehalten, onSchliessen]);

  /* Anhalten bei Zeiger **und** Fokus. Der Fokusfall ist der wichtigere: wer
     mit der Tastatur zur Schaltflaeche „Rueckgaengig" navigiert, darf sie
     nicht unter dem Finger verlieren. */
  const anhalten = () => setzeAngehalten(true);
  const weiter = () => setzeAngehalten(false);

  return (
    <div
      role={art === 'fehler' ? 'alert' : 'status'}
      onMouseEnter={anhalten}
      onMouseLeave={weiter}
      onFocusCapture={anhalten}
      onBlurCapture={weiter}
      style={stil(
        'display:flex;align-items:flex-start;gap:11px;padding:12px 12px 12px 14px;' +
        'background:var(--surface-raised);border:1px solid var(--border-subtle);' +
        'border-radius:var(--r-lg);box-shadow:var(--e2);' +
        'animation:einblenden .2s cubic-bezier(.2,0,0,1)',
      )}
    >
      <span style={stil(
        'flex:0 0 8px;width:8px;height:8px;margin-top:7px;border-radius:999px;background:var(' +
        (art === 'fehler' ? '--danger' : art === 'hinweis' ? '--warning' : '--success') + ')',
      )} />
      <span style={stil('flex:1;min-width:0')}>
        <span style={stil('display:block;font-size:13px;font-weight:600')}>{meldung.titel}</span>
        {meldung.text && (
          <span style={stil('display:block;font-size:12px;color:var(--text-secondary);line-height:17px')}>
            {meldung.text}
          </span>
        )}
      </span>
      {meldung.rueckgaengig && (
        <button
          onClick={() => { meldung.rueckgaengig?.(); onSchliessen(id); }}
          style={stil(
            'background:none;border:none;color:var(--brand-strong);font-size:12px;' +
            'font-weight:500;cursor:pointer;white-space:nowrap',
          )}
        >
          Rückgängig
        </button>
      )}
      <button
        onClick={() => onSchliessen(id)}
        aria-label="Meldung schließen"
        className="auswahlfeld"
        style={stil(
          'width:24px;height:24px;display:grid;place-items:center;background:none;border:none;' +
          'border-radius:var(--r-sm);color:var(--text-tertiary);cursor:pointer',
        )}
      >
        <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}
